// AI director (plan §24, compressed): scripted opening → stance-based utility play.
// Fairness contract: issues the same SimCommands as the player, validated by the same
// pipeline; reads only public state (no enemy cooldowns/uses).

import { buildingDef, cardDef, spellDef, unitDef } from '../content/registry';
import { TUNING } from '../content/tuning';
import type { AiDifficulty } from '../content/factions';
import { difficultyProfile, type DifficultyProfile } from './difficulty';
import { pickOpening, type Opening, type OpeningAction } from './openings';
import type { CardDef, PlayerId } from '../content/types';
import { dist, vec, type Vec2 } from '../shared/vec';
import type { Sim } from '../sim/sim';
import { canPlayBase } from '../sim/systems/cards';
import { isInSquadRecoveryZone } from '../sim/systems/recovery';
import { footprintOf, type Building, type Squad } from '../sim/state';

type Stance = 'OPENING' | 'DEVELOP' | 'PRESSURE' | 'DEFEND' | 'FINISH';

/**
 * Deck introspection (§24.4): the director resolves card roles from its own deck
 * instead of hardcoding faction card ids, so it can pilot any faction's deck.
 */
interface DeckRoles {
  /** cheapest Ascension-I direct unit — the opening squad */
  openingUnit: string | null;
  /** cheapest building card whose subject has a production roster */
  productionBuilding: string | null;
  /** entity-target support spell (Emergency Masonry / Regrowth pattern) */
  entityHeal: string | null;
  /** circle zone spell with membership buffs (Hold the Line pattern) */
  defensiveZone: string | null;
  trap: string | null;
  /** limited-use siege unit for answering Large/Giant threats */
  antiLarge: string | null;
  /** ranged, non-siege direct unit */
  rangedSupport: string | null;
  /** Ascension-III+ assault unit */
  eliteAssault: string | null;
  /** once-per-match Ascension-IV spell */
  ultimate: string | null;
  /** ritual card */
  ritual: string | null;
  /** sacrifice-blast spell (Reckoning) */
  sacrificeSpell: string | null;
  /** support/economy building cards beyond the main production building */
  supportBuildings: string[];
  /** boss cards sorted by ascension (earliest first) */
  bosses: string[];
}

interface AssaultTarget {
  id: number;
  pos: Vec2;
  /** Reachable ground on the attacker's side of the structure. */
  approach: Vec2;
}

/**
 * A read of the enemy army's shape, used to weight card choice. Built only from public unit
 * defs of squads that already exist on the field — the same information the player can see.
 */
interface ThreatVector {
  melee: number;
  ranged: number;
  siege: number;
  large: number;
  giant: number;
  total: number;
}

interface SquadProgress {
  pos: Vec2;
  commandSignature: string;
  lastProgressAt: number;
}

function resolveDeckRoles(deck: string[]): DeckRoles {
  const cards: CardDef[] = deck.map((id) => cardDef(id));
  // Total order: equal-cost cards tiebreak on id so role resolution never depends on sort
  // stability or on the order the deck happened to be authored in.
  const byCost = (a: CardDef, b: CardDef) =>
    a.cost.power + a.cost.essence - (b.cost.power + b.cost.essence) || a.id.localeCompare(b.id);

  const units = cards.filter((c) => c.category === 'unit');
  // cheapest Asc-I unit, falling back to the cheapest unit at all (Verdant's first
  // direct summon is Ascension II; canPlayBase gates it until a site is held)
  const openingUnit =
    units.filter((c) => c.ascension === 1).sort(byCost)[0]?.id ?? units.sort(byCost)[0]?.id ?? null;
  // main production = cheapest building whose roster trains combat squads (not worker variants)
  const productionBuilding =
    cards
      .filter(
        (c) =>
          c.category === 'building' &&
          (buildingDef(c.subjectId).roster?.entries.some((e) => !unitDef(e.unitId).workerVariant) ?? false),
      )
      .sort(byCost)[0]?.id ?? null;
  const supportBuildings = cards
    .filter((c) => c.category === 'building' && c.id !== productionBuilding)
    .sort(byCost)
    .map((c) => c.id);
  const ritual = cards.find((c) => c.category === 'ritual')?.id ?? null;
  const entityHeal =
    cards.find((c) => {
      if (c.category !== 'spell') return false;
      const sd = spellDef(c.subjectId);
      return (
        sd.targeting.shape === 'entity' &&
        (sd.entityHeal !== undefined || sd.effects.some((e) => e.fx === 'heal' || e.fx === 'tempHealth'))
      );
    })?.id ?? null;
  const sacrificeSpell =
    cards.find((c) => c.category === 'spell' && spellDef(c.subjectId).sacrificeBlast !== undefined)?.id ?? null;
  const defensiveZone =
    cards.find((c) => c.category === 'spell' && spellDef(c.subjectId).zoneBuffs !== undefined)?.id ?? null;
  const trap = cards.find((c) => c.category === 'trap')?.id ?? null;
  const antiLarge =
    units.find((c) => unitDef(c.subjectId).roleTags.includes('siege') && c.useModel.kind === 'limited')?.id ??
    null;
  const rangedSupport =
    units.find((c) => {
      const d = unitDef(c.subjectId);
      return d.roleTags.includes('ranged') && !d.roleTags.includes('siege');
    })?.id ?? null;
  const eliteAssault =
    units.filter((c) => c.ascension >= 3 && c.id !== antiLarge).sort(byCost)[0]?.id ?? null;
  const ultimate = cards.find((c) => c.category === 'spell' && c.ascension === 4 && c.useModel.kind === 'once')?.id ?? null;
  const bosses = cards
    .filter((c) => c.category === 'boss')
    .sort((a, b) => a.ascension - b.ascension || a.id.localeCompare(b.id))
    .map((c) => c.id);

  return {
    openingUnit,
    productionBuilding,
    entityHeal,
    defensiveZone,
    trap,
    antiLarge,
    rangedSupport,
    eliteAssault,
    ultimate,
    ritual,
    sacrificeSpell,
    supportBuildings,
    bosses,
  };
}

export class AiDirector {
  readonly player: PlayerId;
  private sim: Sim;
  private stance: Stance = 'OPENING';
  private decideTimer = 0;
  private cardTimer = 0;
  private squadTimer = 0;
  private openingStep = 0;
  private lastAggressionAt = 0;
  private recallClearAt = -1;
  private mineFieldPlaced = 0;
  private squadProgress = new Map<number, SquadProgress>();
  /** Squads held out of normal orders until their retreat has produced a real recovery. */
  private recoveringSquads = new Set<number>();
  private roles: DeckRoles;
  private readonly profile: DifficultyProfile;
  private readonly cadence: { director: number; cards: number; squads: number };
  private readonly aggressionDelay: number;
  private readonly resourceReserve: number;
  private readonly retreatHealth: number;
  private readonly recoveryReleaseHealth = 0.85; // TEST
  /** chosen deterministically from (seed, player); see openings.ts */
  private readonly opening: Opening;
  /** remaining command budget this second (the APM cap) */
  private actionBudget: number;
  private lastCompositionAt = -Infinity;
  private threat: ThreatVector = { melee: 0, ranged: 0, siege: 0, large: 0, giant: 0, total: 0 };

  constructor(sim: Sim, player: PlayerId, difficulty: AiDifficulty = 'standard') {
    this.sim = sim;
    this.player = player;
    this.profile = difficultyProfile(difficulty);
    this.cadence = this.profile.cadence;
    this.aggressionDelay = this.profile.aggressionDelay;
    this.resourceReserve = this.profile.resourceReserve;
    this.retreatHealth = this.profile.retreatHealth;
    this.opening = pickOpening(sim.state.seed, player, this.profile.openingVariety);
    this.actionBudget = this.profile.actionsPerSecond;
    this.roles = resolveDeckRoles(sim.state.players[player].deck);
  }

  get currentStance(): Stance {
    return this.stance;
  }

  /** Called once per sim tick. */
  update(): void {
    const st = this.sim.state;
    if (st.phase !== 'running') return;
    const dt = TUNING.dt;
    this.decideTimer -= dt;
    this.cardTimer -= dt;
    this.squadTimer -= dt;

    // Refill the per-second command budget (the APM cap). Without this an Easy AI is merely slow
    // to *think*, then executes a full turn of perfect orders instantly the moment it does.
    this.actionBudget = Math.min(
      this.profile.actionsPerSecond,
      this.actionBudget + this.profile.actionsPerSecond * dt,
    );

    // Re-read the enemy composition on the difficulty's own cadence. This is what makes a low
    // difficulty react late to a composition switch without giving it worse information.
    if (st.time - this.lastCompositionAt >= this.profile.compositionRecheck) {
      this.lastCompositionAt = st.time;
      this.threat = this.readThreat();
    }

    if (this.decideTimer <= 0) {
      this.decideTimer = this.cadence.director;
      this.runOpening();
      this.chooseStance();
      this.economy();
      this.production();
      this.watchdogs();
    }
    if (this.cardTimer <= 0) {
      this.cardTimer = this.cadence.cards;
      this.cards();
    }
    if (this.squadTimer <= 0) {
      this.squadTimer = this.cadence.squads;
      this.squads();
    }
  }

  // ---------------------------------------------------------------- helpers

  private get p() {
    return this.sim.state.players[this.player];
  }

  private get enemy(): PlayerId {
    const from = this.myCore()?.pos ?? this.sim.map.corePositions[this.player];
    return this.sim.enemiesOf(this.player)
      .map((player) => ({ player, core: this.sim.coreAlive(player) }))
      .filter((entry) => entry.core)
      .sort((a, b) => dist(from, a.core!.pos) - dist(from, b.core!.pos) || a.player - b.player)[0]?.player
      ?? this.sim.enemiesOf(this.player)[0];
  }

  private mySquads(): Squad[] {
    const out: Squad[] = [];
    for (const s of this.sim.state.squads.values()) if (s.owner === this.player) out.push(s);
    return out;
  }

  private enemySquads(): Squad[] {
    const out: Squad[] = [];
    for (const s of this.sim.state.squads.values()) if (this.sim.areHostile(this.player, s.owner)) out.push(s);
    return out;
  }

  private strength(list: Squad[]): number {
    let v = 0;
    for (const s of list) v += unitDef(s.defId).command * (s.health / s.maxHealth);
    return v;
  }

  private myCore() {
    return this.sim.coreAlive(this.player);
  }

  private homeFlankSite() {
    const core = this.myCore()?.pos ?? this.sim.map.corePositions[this.player];
    return [...this.sim.state.sites].sort((a, b) => dist(a.pos, core) - dist(b.pos, core) || a.id.localeCompare(b.id))[0];
  }

  /**
   * Issue a command, subject to the difficulty's action budget.
   *
   * Config-style commands (toggles, allocation, rally) bypass the budget: they are one-off
   * settings a human sets once, and spending the army-order budget on them would make low
   * difficulties fail to even configure themselves.
   */
  private dispatch(cmd: Parameters<Sim['dispatch']>[0]): void {
    const metered =
      cmd.kind === 'IssueCommand' || cmd.kind === 'PlayCard' || cmd.kind === 'QueueUnit' || cmd.kind === 'QueueWorker';
    if (metered) {
      if (this.actionBudget < 1) return;
      this.actionBudget -= 1;
    }
    this.sim.dispatch(cmd);
  }

  /**
   * Summarise the enemy army by role weight. Uses `command` cost x health fraction as the weight,
   * matching the existing strength() convention, so a full-health elite counts for more than a
   * crippled trash squad.
   */
  private readThreat(): ThreatVector {
    const out: ThreatVector = { melee: 0, ranged: 0, siege: 0, large: 0, giant: 0, total: 0 };
    for (const s of this.enemySquads()) {
      const d = unitDef(s.defId);
      const weight = d.command * (s.health / s.maxHealth);
      out.total += weight;
      if (d.roleTags.includes('siege')) out.siege += weight;
      else if (d.roleTags.includes('ranged') || (d.attack?.range ?? 0) > 8) out.ranged += weight;
      else out.melee += weight;
      if (d.sizeTag === 'giant') out.giant += weight;
      else if (d.sizeTag === 'large') out.large += weight;
    }
    return out;
  }

  /**
   * Which DeckRole best answers what the enemy is fielding, or null when counter-play is off or
   * nothing dominates. Expressed purely in role terms, so it stays faction-agnostic.
   */
  private counterRole(): string | null {
    if (this.profile.counterPlay === 'off') return null;
    const t = this.threat;
    if (t.total <= 0) return null;
    const heavy = (t.large + t.giant) / t.total;
    if (heavy > 0.3 && this.roles.antiLarge) return this.roles.antiLarge;
    if (this.profile.counterPlay === 'large-only') return null;
    // Full counter-play: answer a melee-heavy army with ranged, and a ranged-heavy one by
    // pushing elite assault units that can close and survive the approach.
    if (t.melee / t.total > 0.6 && this.roles.rangedSupport) return this.roles.rangedSupport;
    if (t.ranged / t.total > 0.55 && this.roles.eliteAssault) return this.roles.eliteAssault;
    if (t.siege / t.total > 0.3 && this.roles.eliteAssault) return this.roles.eliteAssault;
    return null;
  }

  private tryPlay(cardId: string | null, targetPos: Vec2, targetEntity?: number): boolean {
    if (!cardId || !this.p.cards[cardId]) return false;
    if (canPlayBase(this.sim, this.player, cardId) !== 'ok') return false;
    this.dispatch({ kind: 'PlayCard', player: this.player, cardId, targetPos, targetEntity });
    return true;
  }

  private summonSpot(): Vec2 {
    const core = this.myCore();
    if (!core) return vec(120, 80);
    const toward = this.homeFlankSite().pos;
    const dir = Math.sign(toward.x - core.pos.x) || 1;
    return this.sim.nav.nearestWalkable(vec(core.pos.x + dir * 8, core.pos.y - 8));
  }

  // ---------------------------------------------------------------- opening (§24.5)

  private runOpening(): void {
    if (this.stance !== 'OPENING') return;
    const t = this.sim.state.time;
    const step = this.opening.steps[this.openingStep];
    if (!step) {
      this.stance = 'DEVELOP';
      return;
    }
    if (t < step.at) return;
    // Every step is skippable, so a faction missing a role — or an opening whose building never
    // becomes affordable — can never stall the AI in OPENING forever.
    if (t > step.skipAt && step.action.kind !== 'develop') {
      this.openingStep++;
      return;
    }

    if (this.runOpeningStep(step.action, t)) this.openingStep++;
  }

  /** Returns true when the step is satisfied and the opening should advance. */
  private runOpeningStep(action: OpeningAction, t: number): boolean {
    switch (action.kind) {
      case 'setup':
        this.dispatch({ kind: 'SetAutoWorker', player: this.player, enabled: true });
        this.dispatch({ kind: 'SetAllocation', player: this.player, power: 90 });
        return true;
      case 'unit':
        return this.tryPlay(this.roles.openingUnit, this.summonSpot());
      case 'production': {
        const core = this.myCore();
        if (!core) return true;
        const flank = this.homeFlankSite();
        const dir = Math.sign(flank.pos.x - core.pos.x) || 1;
        return this.tryPlay(this.roles.productionBuilding, vec(core.pos.x + dir * 14, core.pos.y + 6));
      }
      case 'forwardProduction': {
        // Toward the contested flank rather than beside the Core: more exposed, much closer to
        // the fight, which is the trade the aggressive opening is making.
        const core = this.myCore();
        if (!core) return true;
        const flank = this.homeFlankSite();
        return this.tryPlay(
          this.roles.productionBuilding,
          vec((core.pos.x + flank.pos.x) / 2, (core.pos.y + flank.pos.y) / 2),
        );
      }
      case 'support': {
        const core = this.myCore();
        if (!core) return true;
        const support = this.roles.supportBuildings[0] ?? null;
        if (!support) return true;
        return this.tryPlay(support, vec(core.pos.x - 12, core.pos.y - 8));
      }
      case 'trap': {
        const flank = this.homeFlankSite();
        return this.tryPlay(this.roles.trap, flank.pos);
      }
      case 'captureFlank': {
        const flank = this.homeFlankSite();
        // One group order so the new formation slotting applies — the army arrives as a line
        // instead of trickling onto the site one squad at a time.
        const ids = this.mySquads()
          .filter((s) => s.command.kind === 'idle' || (s.command.kind === 'defend' && s.command.auto))
          .map((s) => s.id);
        if (ids.length === 0) return t > 20;
        this.dispatch({
          kind: 'IssueCommand',
          player: this.player,
          squadIds: ids,
          command: 'capture',
          targetPos: flank.pos,
        });
        return true;
      }
      case 'develop':
        this.stance = 'DEVELOP';
        return true;
      default:
        return true;
    }
  }

  // ---------------------------------------------------------------- stance (§24.2)

  private chooseStance(): void {
    if (this.stance === 'OPENING') return;
    const st = this.sim.state;
    const myStr = this.strength(this.mySquads());
    const enStr = this.strength(this.enemySquads());
    const core = this.myCore();

    // Threatened home? Track unique squads: the old counter could count the same squad
    // once for the Core and again for a nearby owned site, causing stance oscillation.
    const threatsNearHome = new Set<number>();
    if (core) {
      for (const s of this.enemySquads()) {
        if (dist(s.pos, core.pos) < this.sim.map.coreTerritoryRadius + 12) threatsNearHome.add(s.id);
      }
    }
    for (const ally of this.sim.state.players) {
      if (ally.id === this.player || !this.sim.areAllies(ally.id, this.player)) continue;
      const alliedCore = this.sim.coreAlive(ally.id);
      if (!alliedCore) continue;
      for (const s of this.enemySquads()) {
        if (dist(s.pos, alliedCore.pos) < this.sim.map.coreTerritoryRadius + 12) threatsNearHome.add(s.id);
      }
    }
    for (const site of st.sites) {
      if (site.owner !== this.player) continue;
      for (const s of this.enemySquads()) {
        if (dist(s.pos, site.pos) < TUNING.defendRadius + 6) threatsNearHome.add(s.id);
      }
    }

    const homeThreats = [...threatsNearHome]
      .map((id) => st.squads.get(id))
      .filter((s): s is Squad => s !== undefined);
    const majorThreat = homeThreats.some((s) => {
      const size = unitDef(s.defId).sizeTag;
      return s.isBoss || size === 'large' || size === 'giant';
    });
    const structureUnderAttack = [...st.buildings.values()].some(
      (b) => this.sim.areAllies(b.owner, this.player) && b.state !== 'destroyed' && st.time - b.lastDamagedAt < 6,
    );

    const enemyCore = this.sim.coreAlive(this.enemy);
    if (this.stance === 'FINISH') return; // locked (watchdog)
    if (homeThreats.length >= 2 || majorThreat || (structureUnderAttack && homeThreats.length > 0)) {
      this.stance = 'DEFEND';
    } else if (enemyCore && enemyCore.health < enemyCore.maxHealth * 0.5 && myStr >= enStr) this.stance = 'FINISH';
    else if (myStr > enStr * 1.25 && myStr >= 9) this.stance = 'PRESSURE';
    else this.stance = 'DEVELOP';
  }

  // ---------------------------------------------------------------- economy (§24.4)

  private economy(): void {
    const st = this.sim.state;
    const p = this.p;
    if (!p.autoWorker) this.dispatch({ kind: 'SetAutoWorker', player: this.player, enabled: true });

    // allocation by demand: Essence matters from Ascension II (A-16)
    const wantEssence = p.ascensionDiscovered >= 2 && p.essence < 80;
    const target = wantEssence ? 70 : 90;
    if (Math.abs(p.allocationPower - target) > 5) {
      this.dispatch({ kind: 'SetAllocation', player: this.player, power: target });
    }

    // raid response: recall when enemies near multiple workers (§24.4 Economy)
    let workersThreatened = 0;
    for (const w of st.workers.values()) {
      if (w.owner !== this.player || w.state === 'sheltered') continue;
      for (const s of this.enemySquads()) {
        if (dist(s.pos, w.pos) < 14) {
          workersThreatened++;
          break;
        }
      }
    }
    const localDefense = this.mySquads().filter((s) => {
      const core = this.myCore();
      return core && dist(s.pos, core.pos) < this.sim.map.coreTerritoryRadius + 10;
    }).length;
    const recallActive = [...st.buildings.values()].some(
      (b) => b.owner === this.player && b.recallActive,
    );
    if (workersThreatened >= 2 && localDefense < 2 && !recallActive) {
      this.dispatch({ kind: 'RecallWorkers', player: this.player });
      this.recallClearAt = -1;
    } else if (recallActive) {
      if (workersThreatened === 0) {
        if (this.recallClearAt < 0) this.recallClearAt = st.time;
        if (st.time - this.recallClearAt > 5) {
          this.dispatch({ kind: 'ResumeWork', player: this.player });
          this.recallClearAt = -1;
        }
      } else {
        this.recallClearAt = -1;
      }
    }

    // opt site towers into worker training (towers no longer auto-train by default)
    for (const b of st.buildings.values()) {
      if (b.owner !== this.player || b.state !== 'active' || !buildingDef(b.defId).isSiteTower) continue;
      if (!b.workerAuto) this.dispatch({ kind: 'ToggleTowerWorker', player: this.player, buildingId: b.id });
    }

    // repair damaged structures when the area is calm
    for (const b of st.buildings.values()) {
      if (b.owner !== this.player || b.state === 'destroyed') continue;
      if (b.health > b.maxHealth * 0.75) continue;
      let danger = false;
      for (const s of this.enemySquads()) {
        if (dist(s.pos, b.pos) < 20) {
          danger = true;
          break;
        }
      }
      if (danger && !b.isCore) continue;
      const idle = [...st.workers.values()]
        .filter((w) => w.owner === this.player && (w.state === 'idle' || w.state === 'toDeposit'))
        .slice(0, 2)
        .map((w) => w.id);
      if (idle.length > 0) {
        this.dispatch({ kind: 'RepairOrder', player: this.player, workerIds: idle, buildingId: b.id });
      }
      break; // one repair intent per decision
    }
  }

  // ---------------------------------------------------------------- production (§24.4, §16.7)

  private production(): void {
    const st = this.sim.state;
    const p = this.p;
    for (const b of st.buildings.values()) {
      if (b.owner !== this.player || b.state !== 'active') continue;
      const bd = buildingDef(b.defId);
      if (!bd.roster) continue;
      // auto-produce every ascension-unlocked roster row
      for (const entry of bd.roster.entries) {
        if (p.ascensionPlayable >= entry.ascension && !b.autoProduce[entry.unitId]) {
          this.dispatch({ kind: 'ToggleAutoProduce', player: this.player, buildingId: b.id, unitId: entry.unitId });
        }
      }
      // upgrade when flush (≥2 squads of the building's base row exist, §16.7)
      const baseRow = bd.roster.entries.find((e) => e.ascension === 1);
      const baseCount = baseRow
        ? this.mySquads().filter((s) => s.defId === baseRow.unitId).length
        : 0;
      if (!b.upgradeDone && b.upgradeRemaining <= 0 && baseCount >= 2 && p.power > 450) {
        this.dispatch({ kind: 'BuyUpgrade', player: this.player, buildingId: b.id });
      }
    }
    // arm site towers when the economy allows
    for (const b of st.buildings.values()) {
      if (b.owner !== this.player || b.state !== 'active' || !buildingDef(b.defId).isSiteTower) continue;
      if (!b.upgradeDone && b.upgradeRemaining <= 0 && p.power > 400) {
        this.dispatch({ kind: 'BuyUpgrade', player: this.player, buildingId: b.id });
        break;
      }
    }
    // Core annexes when the bank is deep (array order = the faction's natural opening)
    const core = this.myCore();
    if (core && !core.upgradeInProgress && p.power > 650 && p.essence > 80) {
      const track = buildingDef(core.defId).upgrades ?? [];
      const next = track.find((u) => !core.upgradesDone.includes(u.id));
      if (next && p.power >= next.cost.power + this.resourceReserve && p.essence >= next.cost.essence) {
        this.dispatch({
          kind: 'BuyCoreUpgrade',
          player: this.player,
          buildingId: core.id,
          upgradeId: next.id,
        });
      }
    }
    // Core Annex System: put annex products to work
    for (const s of this.mySquads()) {
      const ud = unitDef(s.defId);
      if (ud.deploysInto && this.sim.inFriendlyTerritory(s.pos, this.player)) {
        // deploy carriers where they stand — free forward structures beat idle carts
        this.dispatch({ kind: 'DeployCarrier', player: this.player, squadId: s.id });
      } else if (ud.rootable && !s.treeRooted && s.uprootRemaining <= 0) {
        this.dispatch({ kind: 'ToggleRoot', player: this.player, squadId: s.id });
      }
    }
    // Ashen: run the Blood Tithe once the workforce can carry it
    if (!p.annex.titheActive && !p.annex.apotheosisUsed && !p.annex.apotheosis) {
      const hasCrucible = [...this.sim.coreAnnexes(this.player)].some((u) => u.bloodTithe);
      if (hasCrucible) {
        let workers = 0;
        for (const w of st.workers.values()) if (w.owner === this.player && !w.dead) workers++;
        if (workers >= 10) this.dispatch({ kind: 'SetTithe', player: this.player, active: true });
      }
    }

    // claim a lone ruin into a castle wall when a squad is standing near one
    if (p.power > 350) {
      for (const ruin of st.ruins) {
        if (ruin.buildingId !== null || ruin.group !== null || st.time < ruin.cooldownUntil) continue;
        const near = this.mySquads().some((s) => {
          const dx = s.pos.x - ruin.pos.x;
          const dy = s.pos.y - ruin.pos.y;
          return dx * dx + dy * dy < 81;
        });
        if (near) {
          this.dispatch({ kind: 'ClaimRuin', player: this.player, ruinId: ruin.id });
          break;
        }
      }
      // raise the whole base ring at once once it's clearly affordable
      const baseGroup = this.sim.baseWallGroup(this.player);
      const baseRuins = st.ruins.filter(
        (r) => r.group === baseGroup && r.buildingId === null && st.time >= r.cooldownUntil,
      );
      const baseCost = baseRuins.reduce((sum, r) => sum + r.cost, 0);
      if (baseRuins.length > 0 && p.power > baseCost + this.resourceReserve) {
        this.dispatch({ kind: 'BuildBaseWall', player: this.player, group: baseGroup });
      }
    }
  }

  // ---------------------------------------------------------------- cards (§24.4 Deck)

  private cards(): void {
    if (this.stance === 'OPENING') return;
    const st = this.sim.state;
    const p = this.p;
    const army = this.mySquads().filter((s) => !s.isBoss);

    // Emergency Masonry on a wounded building
    let wounded: { id: number; frac: number } | null = null;
    for (const b of st.buildings.values()) {
      if (b.owner !== this.player || b.state === 'destroyed') continue;
      const frac = b.health / b.maxHealth;
      if (frac < 0.6 && (!wounded || frac < wounded.frac)) wounded = { id: b.id, frac };
    }
    if (wounded) {
      const b = st.buildings.get(wounded.id)!;
      if (this.tryPlay(this.roles.entityHeal, b.pos, b.id)) return;
    }

    // second production building in DEVELOP once economy is healthy
    const prodSubject =
      this.roles.productionBuilding !== null ? cardDef(this.roles.productionBuilding).subjectId : null;
    const myProduction = [...st.buildings.values()].filter(
      (b) => b.owner === this.player && b.defId === prodSubject && b.state !== 'destroyed',
    );
    if (this.stance === 'DEVELOP' && myProduction.length < 1) {
      const core = this.myCore();
      if (core) {
        const flank = this.homeFlankSite();
        const dir = Math.sign(flank.pos.x - core.pos.x) || 1;
        if (this.tryPlay(this.roles.productionBuilding, vec(core.pos.x + dir * 14, core.pos.y + 6))) return;
      }
    }
    if (myProduction.length >= 1 && p.power > 550) {
      // forward production at owned flank
      const flank = this.homeFlankSite();
      if (flank.owner === this.player && myProduction.length < 2) {
        if (this.tryPlay(this.roles.productionBuilding, vec(flank.pos.x, flank.pos.y + 10))) return;
      }
    }

    // opening unit to round out the army
    if (army.length < 3 || (this.stance === 'PRESSURE' && army.length < 6)) {
      if (this.tryPlay(this.roles.openingUnit, this.summonSpot())) return;
    }

    // Composition counter-play. This rung used to be a single binary test — "enemy fields
    // anything large or giant, therefore play the siege card" — which was the AI's entire
    // response to army composition. It now consults the threat vector, which weighs melee,
    // ranged, siege, large, and giant share by Command cost x health and answers whichever
    // dominates. Easy has counter-play off entirely and falls through to the old behaviour;
    // Standard keeps the large-only read; Hard uses the full vector.
    const counter = this.counterRole();
    if (counter && this.tryPlay(counter, this.summonSpot())) return;

    // siege answer during assaults, and the fallback for difficulties with counter-play off
    const enemyHasLarge = this.threat.large + this.threat.giant > 0;
    if ((enemyHasLarge || this.stance === 'FINISH') && this.tryPlay(this.roles.antiLarge, this.summonSpot()))
      return;

    // trap at the home flank site when defending/developing
    if (
      (this.stance === 'DEFEND' || this.stance === 'DEVELOP') &&
      this.mineFieldPlaced < 2 &&
      this.homeFlankSite().owner === this.player
    ) {
      const flank = this.homeFlankSite();
      const towardEnemy = this.player === 1 ? -1 : 1;
      const spot = vec(flank.pos.x + towardEnemy * 10, flank.pos.y);
      if (this.tryPlay(this.roles.trap, spot)) {
        this.mineFieldPlaced++;
        return;
      }
    }

    // defensive zone on a clash at an owned site
    for (const site of st.sites) {
      if (site.owner !== this.player) continue;
      const friendly = army.filter((s) => dist(s.pos, site.pos) < 14).length;
      const hostile = this.enemySquads().filter((s) => dist(s.pos, site.pos) < 14).length;
      if (friendly >= 2 && hostile >= 2) {
        if (this.tryPlay(this.roles.defensiveZone, site.pos)) return;
      }
    }

    // ranged support once the frontline exists
    const rangedCount = army.filter((s) => (unitDef(s.defId).attack?.range ?? 0) > 4).length;
    if (army.length >= 2 && rangedCount < 2) {
      if (this.tryPlay(this.roles.rangedSupport, this.summonSpot())) return;
    }

    // elite assault unit to spearhead attacks at Ascension III
    if ((this.stance === 'PRESSURE' || this.stance === 'FINISH') && p.ascensionPlayable >= 3) {
      if (this.tryPlay(this.roles.eliteAssault, this.summonSpot())) return;
    }

    // support/economy buildings (Guild, Foundry, Depot) when the bank is comfortable
    if ((this.stance === 'DEVELOP' || this.stance === 'PRESSURE') && p.power > 500) {
      for (const bcard of this.roles.supportBuildings) {
        const def = cardDef(bcard);
        if (p.power < def.cost.power + 250) continue;
        const core = this.myCore();
        if (!core) break;
        const flank = this.homeFlankSite();
        const dir = Math.sign(flank.pos.x - core.pos.x) || 1;
        if (this.tryPlay(bcard, vec(core.pos.x + dir * 10, core.pos.y - 12))) return;
      }
    }

    // ritual when pressing with a real army behind it (channel at own core)
    if ((this.stance === 'PRESSURE' || this.stance === 'FINISH') && army.length >= 4) {
      const core = this.myCore();
      if (core && this.tryPlay(this.roles.ritual, vec(core.pos.x + 10, core.pos.y))) return;
    }

    // ultimate on the enemy core approach when finishing
    if (this.stance === 'FINISH' && p.ascensionPlayable >= 4) {
      const enemyCore = this.sim.coreAlive(this.enemy);
      if (enemyCore && this.tryPlay(this.roles.ultimate, enemyCore.pos)) return;
    }

    // bosses when pressing with an army behind them (earliest tier first)
    if ((this.stance === 'PRESSURE' || this.stance === 'FINISH') && army.length >= 3) {
      for (const bossCard of this.roles.bosses) {
        if (p.ascensionPlayable >= cardDef(bossCard).ascension && this.tryPlay(bossCard, this.summonSpot()))
          return;
      }
    }
  }

  // ---------------------------------------------------------------- squads (§24.4 Squad/Site/Defense/Boss)

  private squads(): void {
    if (this.stance === 'OPENING') return;
    const st = this.sim.state;
    const squads = this.mySquads();
    if (squads.length === 0) return;
    // Commands are queued and only apply on the next sim tick. Reserve a squad as soon
    // as this pass queues an intent for it, otherwise a later phase can overwrite a
    // withdraw/escort/mount before the first order ever takes effect.
    const reserved = new Set<number>();

    const liveSquadIds = new Set(squads.map((s) => s.id));
    for (const id of this.recoveringSquads) {
      if (!liveSquadIds.has(id)) this.recoveringSquads.delete(id);
    }

    // Group-level retreat (Standard+). Retreat was purely per-squad: an outmatched army melted
    // one squad at a time as each individually crossed its health threshold, feeding the fight
    // instead of disengaging from it. If the engaged group as a whole is losing, pull all of it
    // back at once, before the individual thresholds ever trigger.
    if (this.profile.usesGroupRetreat && this.stance !== 'FINISH') {
      const engaged = squads.filter((s) => !s.isBoss && s.engaged && !this.recoveringSquads.has(s.id));
      if (engaged.length >= 3) {
        // Only count the enemies actually in this fight, not their whole army.
        const nearby = this.enemySquads().filter((e) =>
          engaged.some((own) => dist(e.pos, own.pos) < 18),
        );
        const mine = this.strength(engaged);
        const theirs = this.strength(nearby);
        if (theirs > 0 && mine < theirs * this.profile.groupRetreatRatio) {
          for (const s of engaged) this.recoveringSquads.add(s.id);
        }
      }
    }

    // Dying squads sacrifice-blast among enemies or enter a persistent recovery roster.
    // Reaching friendly territory no longer makes a still-wounded squad immediately eligible
    // for another assault, which previously caused withdraw/auto-defend to alternate forever.
    for (const s of squads) {
      if (s.isBoss) continue;
      const healthFraction = s.health / s.maxHealth;
      if (this.stance !== 'FINISH' && healthFraction < this.retreatHealth && !this.recoveringSquads.has(s.id)) {
        if (this.roles.sacrificeSpell !== null) {
          const enemiesNear = this.enemySquads().filter((e) => dist(e.pos, s.pos) < 6).length;
          if (enemiesNear >= 1 && this.tryPlay(this.roles.sacrificeSpell, s.pos, s.id)) {
            reserved.add(s.id);
            continue;
          }
        }
        this.recoveringSquads.add(s.id);
      }

      if (!this.recoveringSquads.has(s.id)) continue;
      if (healthFraction >= this.recoveryReleaseHealth) {
        this.recoveringSquads.delete(s.id);
        continue;
      }

      reserved.add(s.id);
      const threatNearby = this.enemySquads().some((enemy) => dist(enemy.pos, s.pos) < 14);
      if (isInSquadRecoveryZone(this.sim, s) && !threatNearby) {
        // Stop on walkable ground inside the recovery zone. Reissuing Withdraw here points
        // at the blocked center of the Core and is the source of the running-in-place loop.
        if (s.command.kind !== 'idle') {
          this.dispatch({ kind: 'IssueCommand', player: this.player, squadIds: [s.id], command: 'idle' });
        }
      } else if (s.command.kind !== 'withdraw') {
        this.dispatch({ kind: 'IssueCommand', player: this.player, squadIds: [s.id], command: 'withdraw' });
      }
    }

    // boss escort: keep 2 infantry escorting a living boss (§24.4 Boss)
    const boss = squads.find((s) => s.isBoss);
    if (boss) {
      // mount idle mountable squads onto a boss with free slots (scripted convenience)
      const slots = unitDef(boss.defId).mountSlots ?? 0;
      if (slots > 0) {
        let mounted = 0;
        for (const s of squads) if (s.mountedOnSquad === boss.id) mounted++;
        if (mounted < slots) {
          const candidates = squads.filter(
            (s) =>
              !s.isBoss &&
              !reserved.has(s.id) &&
              s.mountedOnWall === null &&
              s.mountedOnSquad === null &&
              s.escortOf === null &&
              s.command.kind !== 'withdraw' &&
              (unitDef(s.defId).mountableOn?.includes(boss.defId) ?? false) &&
              dist(s.pos, boss.pos) <= TUNING.mountRange,
          );
          if (candidates.length > 0) {
            const riders = candidates.slice(0, slots - mounted);
            this.dispatch({
              kind: 'MountSquad',
              player: this.player,
              squadIds: riders.map((s) => s.id),
              hostId: boss.id,
            });
            for (const rider of riders) reserved.add(rider.id);
          }
        }
      }
      const escorts = squads.filter((s) => s.escortOf === boss.id);
      if (escorts.length < 2) {
        const candidates = squads
          .filter(
            (s) =>
              !s.isBoss &&
              !reserved.has(s.id) &&
              s.escortOf === null &&
              s.mountedOnWall === null &&
              s.mountedOnSquad === null &&
              s.command.kind !== 'withdraw',
          )
          .sort((a, b) => dist(a.pos, boss.pos) - dist(b.pos, boss.pos) || a.id - b.id)
          .slice(0, 2 - escorts.length);
        for (const c of candidates) {
          this.dispatch({
            kind: 'IssueCommand',
            player: this.player,
            squadIds: [c.id],
            command: 'escort',
            targetEntity: boss.id,
          });
          reserved.add(c.id);
        }
      }
      // boss target: enemy structures toward the enemy core
      if (boss.command.kind === 'idle' || (boss.command.kind === 'defend' && boss.command.auto)) {
        const target = this.pickAssaultTarget(boss.pos);
        if (target) {
          this.issueAssaultOrder(boss, target);
          this.lastAggressionAt = st.time;
        }
      }
    }

    const idle = squads.filter(
      (s) =>
        !s.isBoss &&
        !reserved.has(s.id) &&
        s.escortOf === null &&
        s.mountedOnWall === null &&
        s.mountedOnSquad === null &&
        (s.command.kind === 'idle' || (s.command.kind === 'defend' && s.command.auto)),
    );

    switch (this.stance) {
      case 'DEFEND': {
        // send idles to the hottest threatened point
        const hot = this.hottestThreat();
        if (hot && idle.length > 0) {
          this.dispatch({
            kind: 'IssueCommand',
            player: this.player,
            squadIds: idle.map((s) => s.id),
            command: 'attack',
            targetPos: hot,
          });
        }
        break;
      }
      case 'DEVELOP': {
        // expand: capture best unowned site; garrison owned ones
        const target = this.bestCaptureSite();
        if (target && idle.length > 0) {
          const group = idle.slice(0, Math.max(1, Math.min(2, idle.length)));
          this.dispatch({
            kind: 'IssueCommand',
            player: this.player,
            squadIds: group.map((s) => s.id),
            command: 'capture',
            targetPos: target.pos,
          });
          this.lastAggressionAt = st.time;
        }
        break;
      }
      case 'PRESSURE':
      case 'FINISH': {
        let group = idle;
        // Ascension, command caps, and card tiers are all gated on held sites. A
        // production-heavy faction (Verdant) reaches PRESSURE on raw strength and then
        // only ever staged assaults — the army massed at home while ascension stalled.
        // While pressing, keep peeling a small claim detachment at the best open site.
        if (this.stance === 'PRESSURE' && this.sim.sitesOwned(this.player) < 3 && group.length > 2) {
          const site = this.bestCaptureSite();
          if (site) {
            const claimers = group.slice(0, 2);
            group = group.slice(2);
            this.dispatch({
              kind: 'IssueCommand',
              player: this.player,
              squadIds: claimers.map((s) => s.id),
              command: 'capture',
              targetPos: site.pos,
            });
            this.lastAggressionAt = st.time;
          }
        }
        // Multi-prong harassment (Hard only). Peel the fastest squads at the enemy's workers
        // while the main body commits elsewhere — the raid command already prioritises workers,
        // so this reuses existing behaviour rather than adding a new one.
        if (this.profile.maxProngs > 1 && this.stance === 'PRESSURE' && group.length >= 5) {
          const raiders = [...group]
            .sort((a, b) => unitDef(b.defId).moveSpeed - unitDef(a.defId).moveSpeed || a.id - b.id)
            .slice(0, 2);
          const raidIds = new Set(raiders.map((r) => r.id));
          const enemyCore = this.sim.coreAlive(this.enemy);
          if (enemyCore) {
            group = group.filter((s) => !raidIds.has(s.id));
            this.dispatch({
              kind: 'IssueCommand',
              player: this.player,
              squadIds: raiders.map((s) => s.id),
              command: 'raid',
              targetPos: enemyCore.pos,
            });
            for (const r of raiders) reserved.add(r.id);
          }
        }

        // Group up, then assault. Staging (Standard+) waits for a real fist to form instead of
        // feeding squads in one at a time, and issues ONE group order so the formation slotting
        // applies — the army arrives as a line rather than a conga. Easy skips staging entirely
        // and keeps the old trickle.
        const threshold = this.stance === 'FINISH'
          ? 1
          : this.profile.usesStaging
            ? this.profile.stagingThreshold
            : 3;
        if (group.length >= threshold) {
          const target = this.pickAssaultTarget(group[0].pos);
          if (target) {
            for (const s of group) this.issueAssaultOrder(s, target);
            this.lastAggressionAt = st.time;
          }
        } else {
          // stage toward center while the army builds
          const target = this.bestCaptureSite();
          if (target && group.length > 0) {
            this.dispatch({
              kind: 'IssueCommand',
              player: this.player,
              squadIds: group.map((s) => s.id),
              command: 'capture',
              targetPos: target.pos,
            });
            this.lastAggressionAt = st.time;
          }
        }
        break;
      }
      default:
        break;
    }
  }

  private bestCaptureSite() {
    const st = this.sim.state;
    const core = this.myCore();
    if (!core) return null;
    let best: (typeof st.sites)[number] | null = null;
    let bestScore = -Infinity;
    for (const site of st.sites) {
      if (site.owner !== -1 && this.sim.areAllies(site.owner, this.player)) continue;
      // §12.6-flavored valuation
      let score = 1;
      if (site.owner === -1) {
        const allyAlreadyCapturing = [...st.squads.values()].some((squad) =>
          squad.owner !== this.player && this.sim.areAllies(squad.owner, this.player) && squad.command.kind === 'capture' &&
          squad.command.targetPos !== undefined && dist(squad.command.targetPos, site.pos) < 8,
        );
        if (allyAlreadyCapturing) score -= 4;
      }
      const sitesOwned = this.sim.sitesOwned(this.player);
      if (sitesOwned < 3) score += 0.6; // ascension need
      score -= dist(site.pos, core.pos) * 0.01;
      let threat = 0;
      for (const s of this.enemySquads()) if (dist(s.pos, site.pos) < 20) threat++;
      score -= threat * 0.5;
      if (site.owner !== -1 && this.sim.areHostile(this.player, site.owner)) score -= 0.4;
      if (score > bestScore) {
        bestScore = score;
        best = site;
      }
    }
    return best;
  }

  /**
   * Find walkable ground immediately outside the near face of a structure. A path to
   * this point proves the attacker can reach the structure from its current connected
   * nav region. This matters for hostile gates: a Core inside an intact base ring is
   * geometrically close but not reachable until one of the walls is breached.
   */
  private reachableApproach(from: Vec2, building: Building): Vec2 | null {
    const fp = footprintOf(buildingDef(building.defId), building.rot);
    const halfW = fp.w / 2 + 1.25;
    const halfH = fp.h / 2 + 1.25;
    let dx = from.x - building.pos.x;
    let dy = from.y - building.pos.y;
    if (Math.abs(dx) + Math.abs(dy) < 0.001) {
      const home = this.myCore()?.pos ?? this.sim.map.corePositions[this.player];
      dx = home.x - building.pos.x;
      dy = home.y - building.pos.y;
    }
    const edgeScale = 1 / Math.max(Math.abs(dx) / halfW, Math.abs(dy) / halfH, 0.001);
    const raw = vec(building.pos.x + dx * edgeScale, building.pos.y + dy * edgeScale);
    const approach = this.sim.nav.nearestWalkable(raw, 3, this.player, from);
    if (!this.sim.nav.isWalkable(approach, this.player)) return null;
    return this.sim.nav.canReach(from, approach, this.player) ? approach : null;
  }

  private pickAssaultTarget(from: Vec2): AssaultTarget | null {
    const enemyBuildings = [...this.sim.state.buildings.values()].filter(
      (b) => this.sim.areHostile(this.player, b.owner) && b.state !== 'destroyed',
    );
    // FINISH does mean "kill the Core", but only after proving a route exists. Once a
    // breach opens, take it immediately instead of dismantling every nearby wall first.
    if (this.stance === 'FINISH') {
      const core = enemyBuildings.find((b) => b.isCore);
      if (core) {
        const approach = this.reachableApproach(from, core);
        if (approach) return { id: core.id, pos: { ...core.pos }, approach };
      }
    }

    const candidates = enemyBuildings
      .filter((b) => this.stance !== 'FINISH' || !b.isCore)
      .map((building) => {
        const kind = buildingDef(building.defId).kind;
        let score = dist(from, building.pos);
        // Breachable walls are useful objectives rather than low-value clutter. In
        // FINISH, prefer the Core only once reachable; never pin through an intact ring.
        if (kind === 'wall') score *= 0.72;
        if (building.isCore) score *= 1.2;
        if (building.state !== 'active') score *= 0.9;
        return { building, score };
      })
      .sort((a, b) => a.score - b.score || a.building.id - b.building.id);

    for (const { building } of candidates) {
      const approach = this.reachableApproach(from, building);
      if (approach) return { id: building.id, pos: { ...building.pos }, approach };
    }
    return null;
  }

  /**
   * Long-range assaults are attack-moves to reachable ground, so squads fight blockers
   * en route and naturally acquire a wall. Only pin the structure once already close;
   * a far-away entity pin is what left assaults rotating against an unreachable Core.
   */
  private issueAssaultOrder(s: Squad, target: AssaultTarget, forceAttackMove = false): void {
    const building = this.sim.state.buildings.get(target.id);
    if (!building) return;
    const fp = footprintOf(buildingDef(building.defId), building.rot);
    const dx = Math.max(0, Math.abs(s.pos.x - building.pos.x) - fp.w / 2);
    const dy = Math.max(0, Math.abs(s.pos.y - building.pos.y) - fp.h / 2);
    const distanceToFootprint = Math.hypot(dx, dy);
    const range = unitDef(s.defId).attack?.range ?? 1.5;
    const focusNow = !forceAttackMove && distanceToFootprint <= range + 1.5;
    this.dispatch({
      kind: 'IssueCommand',
      player: this.player,
      squadIds: [s.id],
      command: 'attack',
      targetPos: focusNow ? target.pos : target.approach,
      targetEntity: focusNow ? target.id : undefined,
    });
  }

  private hottestThreat(): Vec2 | null {
    const core = this.myCore();
    const points: Vec2[] = [];
    if (core) points.push(core.pos);
    for (const ally of this.sim.state.players) {
      if (ally.id === this.player || !this.sim.areAllies(ally.id, this.player)) continue;
      const alliedCore = this.sim.coreAlive(ally.id);
      if (alliedCore) points.push(alliedCore.pos);
    }
    for (const s of this.sim.state.sites) if (s.owner === this.player) points.push(s.pos);
    let best: Vec2 | null = null;
    let bestThreat = 0;
    for (const p of points) {
      let threat = 0;
      let centroid = vec(0, 0);
      for (const s of this.enemySquads()) {
        if (dist(s.pos, p) < 26) {
          const weight = Math.max(1, unitDef(s.defId).command * (s.health / s.maxHealth));
          threat += weight;
          centroid = vec(centroid.x + s.pos.x * weight, centroid.y + s.pos.y * weight);
        }
      }
      if (threat > bestThreat) {
        bestThreat = threat;
        best = vec(centroid.x / threat, centroid.y / threat);
      }
    }
    return best;
  }

  private commandSignature(s: Squad): string {
    const tp = s.command.targetPos;
    return [
      s.command.kind,
      s.command.targetEntity ?? -1,
      s.command.siteId ?? '',
      tp ? Math.round(tp.x * 2) : '',
      tp ? Math.round(tp.y * 2) : '',
    ].join(':');
  }

  /** Re-route an offensive squad that has wanted to move but made no progress for 5 s. */
  private recoverStalledAssaults(): void {
    const st = this.sim.state;
    const live = new Set<number>();
    for (const s of this.mySquads()) {
      live.add(s.id);
      const signature = this.commandSignature(s);
      let progress = this.squadProgress.get(s.id);
      if (!progress || progress.commandSignature !== signature) {
        progress = { pos: { ...s.pos }, commandSignature: signature, lastProgressAt: st.time };
        this.squadProgress.set(s.id, progress);
        continue;
      }

      if (dist(s.pos, progress.pos) >= 0.75) {
        progress.pos = { ...s.pos };
        progress.lastProgressAt = st.time;
        continue;
      }

      const offensive = this.stance === 'PRESSURE' || this.stance === 'FINISH';
      const shouldBeMoving = s.command.kind === 'attack' && s.moveTarget !== null;
      const temporarilyStopped =
        s.engaged ||
        st.time - s.lastCombatAt < 2 ||
        s.rootedRemaining > 0 ||
        s.charge !== null ||
        s.mountedOnWall !== null ||
        s.mountedOnSquad !== null;
      if (!offensive || !shouldBeMoving || temporarilyStopped) {
        progress.pos = { ...s.pos };
        progress.lastProgressAt = st.time;
        continue;
      }
      if (st.time - progress.lastProgressAt < 5) continue;

      const target = this.pickAssaultTarget(s.pos);
      if (target) {
        // Force attack-move even if close: this clears a stale entity pin and makes the
        // squad approach from reachable ground before combat reacquires locally.
        this.issueAssaultOrder(s, target, true);
        this.lastAggressionAt = st.time;
      }
      progress.pos = { ...s.pos };
      progress.lastProgressAt = st.time;
    }
    for (const id of this.squadProgress.keys()) if (!live.has(id)) this.squadProgress.delete(id);
  }

  // ---------------------------------------------------------------- watchdogs (§24.9)

  private watchdogs(): void {
    const st = this.sim.state;
    this.recoverStalledAssaults();
    if (st.time > 25 * 60 && this.stance !== 'FINISH') {
      this.stance = 'FINISH'; // guaranteed termination
      return;
    }
    if (
      this.stance !== 'DEFEND' &&
      this.stance !== 'OPENING' &&
      st.time - this.lastAggressionAt > this.aggressionDelay &&
      this.mySquads().length > 0
    ) {
      this.stance = 'PRESSURE';
      const assaultable = this.mySquads().filter(
        (s) =>
          !s.isBoss &&
          !this.recoveringSquads.has(s.id) &&
          s.command.kind !== 'withdraw' &&
          s.escortOf === null &&
          s.mountedOnWall === null &&
          s.mountedOnSquad === null,
      );
      const leader = assaultable[0] ?? this.mySquads().find((s) => s.isBoss) ?? null;
      const target = leader ? this.pickAssaultTarget(leader.pos) : null;
      if (target && assaultable.length > 0) {
        for (const s of assaultable) this.issueAssaultOrder(s, target);
        this.lastAggressionAt = st.time; // avoid re-triggering every tick
      }
    }
  }
}
